import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { pagesApi } from '../services/api'
import { useLanguage } from '../context/LanguageContext'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import SubPagesGrid from '../components/pages/SubPagesGrid'
import ProgramSidebar from '../components/pages/ProgramSidebar'
import DivisionSectionSidebar from '../components/pages/DivisionSectionSidebar'
import PageExtras from '../components/pages/PageExtras'
import StoryCardsSection from '../components/pages/StoryCardsSection'
import Button from '../components/ui/Button'
import AccreditationsPageContent from '../components/pages/AccreditationsPageContent'
import MayarDifferencePageContent from '../components/pages/MayarDifferencePageContent'
import LeadershipPageContent from '../components/pages/LeadershipPageContent'
import { PageSlugs, PROGRAM_PAGES, DIVISION_SECTION_SLUGS } from '../constants/pageSlugs'
import { resolveStoryCardType } from '../constants/storyCards'
import PageMeta from '../components/seo/PageMeta'
import CmsHtmlContent, { isHtmlContent } from '../components/cms/CmsHtmlContent'
import MediaImage from '../components/ui/MediaImage'
import { resolveMediaUrl } from '../utils/media'

const CUSTOM_CONTENT_SLUGS = [
  PageSlugs.accreditations,
  PageSlugs.mayarDifference,
  PageSlugs.leadership,
]

const PAGE_HERO_BACKGROUNDS = {
  [PageSlugs.admissionRegistration]: '/slider/1430.jpg',
  [PageSlugs.misKhalda]: '/pages/mis-khalda/hero.jpg',
  [PageSlugs.serviceTransportation]: '/pages/services/transportation-hero.jpg',
}

const PAGE_HERO_OBJECT_POSITION = {
  [PageSlugs.misKhalda]: 'center 28%',
}

export default function PageTemplate() {
  const { slug } = useParams()
  const { t } = useTranslation()
  const { localized } = useLanguage()
  const [page, setPage] = useState(null)
  const [relatedPages, setRelatedPages] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const isCustomContentPage = CUSTOM_CONTENT_SLUGS.includes(slug)
  const isAccreditationsPage = slug === PageSlugs.accreditations
  const isMayarDifferencePage = slug === PageSlugs.mayarDifference
  const isLeadershipPage = slug === PageSlugs.leadership
  const resolvedStoryCardType = page ? resolveStoryCardType(page, slug) : null
  const isStoryCardPage = Boolean(resolvedStoryCardType)
  const isWideContentPage =
    slug === PageSlugs.misKhalda || isMayarDifferencePage || isStoryCardPage
  const heroBackground = resolveMediaUrl(page?.hero_image || PAGE_HERO_BACKGROUNDS[slug])
  const heroObjectPosition = PAGE_HERO_OBJECT_POSITION[slug]

  useEffect(() => {
    setLoading(true)
    setError(false)
    pagesApi
      .getBySlug(slug)
      .then(({ data }) => {
        setPage(data.data || data)
        setRelatedPages(data.related_pages || [])
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [slug])

  if (loading && !isCustomContentPage) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center header-offset">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if ((error || !page) && !isCustomContentPage) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 header-offset text-center">
        <h1 className="mb-4 text-3xl font-bold text-primary">{t('pages.notFound')}</h1>
        <p className="text-text/60">{t('pages.notFoundDesc')}</p>
      </div>
    )
  }

  const title = page ? localized(page, 'title') : t('pages.notFound')
  const heroLabel = page
    ? localized(page, 'hero_label') || page.menu_tab || t('brand.name')
    : t('brand.name')
  const heroExcerpt = page ? localized(page, 'excerpt') : ''
  const content = page ? localized(page, 'content') : ''
  const metaTitle = page ? localized(page, 'meta_title') || localized(page, 'title') : title
  const metaDescription = page
    ? localized(page, 'meta_description') || localized(page, 'excerpt')
    : heroExcerpt
  const isHtml = isHtmlContent(content)
  const contentHasLeaderProfile = /leader-profile/.test(content)
  const showPageImage = page?.image && !contentHasLeaderProfile
  const isProgramPage = PROGRAM_PAGES.includes(slug)
  const isDivisionSectionPage = DIVISION_SECTION_SLUGS.includes(slug)

  const contentBlock = isAccreditationsPage ? (
    <AccreditationsPageContent />
  ) : isMayarDifferencePage ? (
    <MayarDifferencePageContent />
  ) : isLeadershipPage ? (
    <LeadershipPageContent />
  ) : isStoryCardPage ? (
    <>
      {content?.trim() ? (
        isHtml ? (
          <CmsHtmlContent content={content} className="page-content mb-10" />
        ) : (
          <div className="page-content mb-10 whitespace-pre-line text-lg leading-relaxed text-text/75">
            {content}
          </div>
        )
      ) : null}
      <StoryCardsSection type={resolvedStoryCardType} />
      <PageExtras slug={slug} />
    </>
  ) : (
    <>
      {showPageImage && (
        <figure
          className={`mb-10 ${slug === PageSlugs.messageFromCeo ? 'mx-auto max-w-sm text-center' : 'flex justify-center'}`}
        >
          <MediaImage
            src={page.image}
            alt={title}
            className="w-full rounded-2xl border border-border/60 object-cover shadow-lg shadow-primary/10"
          />
          {slug === PageSlugs.messageFromCeo && (
            <figcaption className="mt-4 text-sm font-semibold text-primary">
              Eng. Hani Salah
              <span className="mt-1 block text-xs font-normal text-text/60">
                CEO, Mayar Education
              </span>
            </figcaption>
          )}
        </figure>
      )}

      {isHtml ? (
        <CmsHtmlContent
          content={content}
          className="page-content"
        />
      ) : (
        <div className="page-content whitespace-pre-line text-lg leading-relaxed text-text/75">
          {content}
        </div>
      )}

      {slug === PageSlugs.visionMissionValues && (
        <div className="mt-12 text-center">
          <Button to="/programs" variant="accent" size="lg">
            {t('buttons.explorePrograms')}
          </Button>
        </div>
      )}

      <PageExtras slug={slug} />
    </>
  )

  return (
    <article className="header-offset">
      <PageMeta title={metaTitle} description={metaDescription} path={`/pages/${slug}`} />

      <div className={`relative overflow-hidden text-white ${heroBackground ? 'py-16 sm:py-24 md:py-28' : 'gradient-brand py-12 sm:py-16'}`}>
        {heroBackground ? (
          <>
            <img
              src={heroBackground}
              alt=""
              aria-hidden="true"
              className={`absolute inset-0 h-full w-full object-cover ${heroObjectPosition ? '' : 'object-center'}`}
              style={heroObjectPosition ? { objectPosition: heroObjectPosition } : undefined}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/70" />
            <div className="absolute inset-0 bg-primary/35 mix-blend-multiply" />
          </>
        ) : (
          <div className="absolute inset-0 opacity-20">
            <div className="absolute -end-20 top-0 h-64 w-64 rounded-full bg-accent blur-3xl" />
          </div>
        )}
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
          <p className={`mb-3 text-sm font-semibold uppercase tracking-widest ${heroBackground ? 'text-white/90 drop-shadow-md' : 'text-accent'}`}>
            {heroLabel}
          </p>
          <h1 className="text-3xl font-bold drop-shadow-lg sm:text-4xl md:text-5xl">{title}</h1>
          {heroExcerpt && (
            <p className={`mx-auto mt-4 max-w-2xl text-base drop-shadow-md sm:text-lg ${heroBackground ? 'text-white/90' : 'text-white/80'}`}>{heroExcerpt}</p>
          )}
        </div>
      </div>

      <div className="mx-auto min-w-0 w-full max-w-7xl px-4 py-12 sm:px-6 sm:py-16">
        {isProgramPage ? (
          <div className="grid min-w-0 w-full gap-8 lg:grid-cols-[240px_1fr] lg:gap-10">
            <ProgramSidebar currentSlug={slug} />
            <div className="min-w-0 max-w-full">{contentBlock}</div>
          </div>
        ) : isDivisionSectionPage ? (
          <div className="grid min-w-0 w-full gap-8 lg:grid-cols-[260px_1fr] lg:gap-10">
            <DivisionSectionSidebar currentSlug={slug} />
            <div className="min-w-0 max-w-full">{contentBlock}</div>
          </div>
        ) : (
          <div className={`mx-auto ${isWideContentPage ? 'max-w-7xl' : 'max-w-4xl'}`}>{contentBlock}</div>
        )}
      </div>

      {page?.show_related_pages && (
        <SubPagesGrid pages={relatedPages} currentSlug={slug} />
      )}
    </article>
  )
}
